import {useMemo, useState} from 'react'

import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {useIsMergeBoxSidePanel} from '../../../contexts/MergeBoxLayoutContext'
import {
  compareChecks,
  countChecksByGroup,
  extractChecksAnalyticsMetadata,
  groupChecks,
} from '../../../helpers/status-check-helpers'
import type {MergeBoxUserPreferences} from '../../../page-data/payloads/merge-box'
import type {CheckStateRollup, StatusCheck} from '../../../page-data/payloads/status-checks'
import {ChecksGroup} from './ChecksGroup'
import styles from './ExpandedChecks.module.css'
import {StatusCheckRow} from './StatusCheckRow'

const SIDE_PANEL_CHECKS_PREVIEW_COUNT = 7

const CHECKS_LIST_GROUPINGS = {
  FAILURE: ['CANCELLED', 'ERROR', 'FAILURE', 'STALE', 'STARTUP_FAILURE', 'TIMED_OUT'],
  PENDING: ['ACTION_REQUIRED', 'EXPECTED', 'PENDING', 'QUEUED', 'REQUESTED', 'WAITING', '_UNKNOWN_VALUE'],
  IN_PROGRESS: ['IN_PROGRESS'],
  SKIPPED: ['SKIPPED'],
  NEUTRAL: ['NEUTRAL'],
  SUCCESS: ['SUCCESS', 'COMPLETED'],
}

interface Props {
  statusChecks: StatusCheck[]
  statusRollupSummary: CheckStateRollup[]
  mergeBoxUserPreferences: MergeBoxUserPreferences | null
  viewerCanUseCopilotCodingAgent: boolean
  viewerHasCEOrCBAccess: boolean
  onActionSelect?: (actionName: string) => void
}

/**
 * Displays the list of expanded checks
 */
export function ExpandedChecksList({
  statusChecks,
  statusRollupSummary,
  mergeBoxUserPreferences,
  viewerCanUseCopilotCodingAgent,
  viewerHasCEOrCBAccess,
  onActionSelect,
}: Props) {
  const [userPreferences, setUserPreferences] = useState<MergeBoxUserPreferences | null>(
    mergeBoxUserPreferences || null,
  )
  const isSidePanel = useIsMergeBoxSidePanel()
  const [showAllChecks, setShowAllChecks] = useState(false)

  const sortedChecksOrStatuses = useMemo(() => [...statusChecks].sort(compareChecks), [statusChecks])
  const countsByGroup = countChecksByGroup(statusRollupSummary, CHECKS_LIST_GROUPINGS, userPreferences)
  const checksByGroup = groupChecks(
    sortedChecksOrStatuses,
    CHECKS_LIST_GROUPINGS,
    check => check.state,
    userPreferences,
  )
  const hasMoreChecks = isSidePanel && statusChecks.length > SIDE_PANEL_CHECKS_PREVIEW_COUNT
  // Limit the grouped order, rather than the alphabetical input, so failing checks stay first.
  const previewChecks =
    hasMoreChecks && !showAllChecks
      ? new Set(Object.values(checksByGroup).flat().slice(0, SIDE_PANEL_CHECKS_PREVIEW_COUNT))
      : null

  const reserveSpaceForRequiredBadge = statusChecks.some(check => check.isRequired)
  const reserveSpaceForActionBar = statusChecks.some(check => check.copilotCheckRunFailureContext || !!check.targetUrl)

  // count how many groups actually have items
  const groupsWithItemsCount = Object.entries(countsByGroup).reduce<number>(
    (groupsWithItems, [, count]) => (count > 0 ? groupsWithItems + 1 : groupsWithItems),
    0,
  )

  const allChecksSameState =
    statusChecks.length > 0 ? statusChecks.every(check => check.state === statusChecks[0]?.state) : false

  const isToggleVisible =
    groupsWithItemsCount > 1 || (userPreferences?.statusChecksGrouping === 'ungrouped' && !allChecksSameState)

  return (
    <div className={clsx(styles.checksContainer, styles.containPaint)}>
      {Object.entries(countsByGroup)
        .filter(([, count]) => count > 0)
        .map(([groupStatus, count], index) => {
          const checksToShow = previewChecks
            ? checksByGroup[groupStatus]?.filter(check => previewChecks.has(check))
            : checksByGroup[groupStatus]
          if (previewChecks && !checksToShow?.length) return null

          const isFirstGroup = index === 0
          const limitAnimatedChecks = count < 50
          return (
            <ChecksGroup
              key={groupStatus}
              count={count}
              groupStatus={groupStatus}
              isToggleVisible={isToggleVisible}
              analyticsMetadata={extractChecksAnalyticsMetadata(statusRollupSummary)}
              setUserPreferences={setUserPreferences}
              userPreferences={userPreferences}
              showSettingsIcon={userPreferences && isFirstGroup}
              onActionSelect={onActionSelect}
            >
              {checksToShow?.map(checkOrStatus => {
                if (!checkOrStatus) return null

                return (
                  <StatusCheckRow
                    key={`${checkOrStatus.displayName}-${checkOrStatus.state}-${checkOrStatus.targetUrl}-${checkOrStatus.stateChangedAt}`}
                    {...checkOrStatus}
                    shouldAnimate={limitAnimatedChecks}
                    reserveSpaceForActionBar={reserveSpaceForActionBar}
                    reserveSpaceForRequiredBadge={reserveSpaceForRequiredBadge}
                    viewerCanUseCopilotCodingAgent={viewerCanUseCopilotCodingAgent}
                    viewerHasCEOrCBAccess={viewerHasCEOrCBAccess}
                    onActionSelect={onActionSelect}
                  />
                )
              })}
            </ChecksGroup>
          )
        })}
      {hasMoreChecks && (
        <Button
          variant="invisible"
          size="small"
          className={styles.ShowAllChecksButton}
          aria-expanded={showAllChecks}
          onClick={() => setShowAllChecks(showAll => !showAll)}
        >
          {showAllChecks ? 'Show fewer checks' : 'Show all checks'}
        </Button>
      )}
    </div>
  )
}
